// The words on the boat yard forms (Documents): the vessel ENTRY form and EXIT form, in Greek (the original
// ΕΝΤΥΠΟ ΕΙΣΑΓΩΓΗΣ / ΕΞΑΓΩΓΗΣ ΣΚΑΦΟΥΣ), in English, or in both on the same sheet (Greek first, the
// English under each line). One source for the on-screen blank form and the filled-in export
// (boatDocPrint.ts), so the two always say the same thing: both draw from boatDocView(). The language is a
// per-device choice on the Documents page; both languages is the default.

import { useCallback, useState } from 'react';

export type BoatDocKind = 'entry' | 'exit';
/** a language the forms are written in. */
export type FormLang = 'el' | 'en';
/** what the Documents page prints: one language, or both on the same sheet. */
export type DocLang = FormLang | 'both';

export const DOC_LANGS: { value: DocLang; label: string; name: string }[] = [
  { value: 'el', label: 'Ελληνικά', name: 'Greek' },
  { value: 'en', label: 'English', name: 'English' },
  { value: 'both', label: 'Both', name: 'Greek and English' },
];

export interface BoatDocText {
  title: string;
  instrHeading: string;
  instr: string;
  sign: string;
  /** the form's rows, in order. */
  fields: { date: string; boatName: string; model: string; sap: string; phone: string; hin: string; hinHint: string };
}

const FIELDS = {
  el: { date: 'Ημερομηνία:', boatName: 'Όνομα σκάφους:', model: 'Μοντέλο:', sap: 'SAP:', phone: 'Τηλέφωνο:', hin: 'HIN:', hinHint: 'hull identification number' },
  en: { date: 'Date:', boatName: 'Vessel name:', model: 'Model:', sap: 'SAP:', phone: 'Phone:', hin: 'HIN:', hinHint: 'hull identification number' },
} as const;

export const BOAT_DOC_TEXT: Record<FormLang, Record<BoatDocKind, BoatDocText>> = {
  el: {
    entry: {
      title: 'ΕΝΤΥΠΟ ΕΙΣΑΓΩΓΗΣ ΣΚΑΦΟΥΣ',
      instrHeading: 'Οδηγίες',
      instr:
        'Για κάθε σκάφος που εισέρχεται στον χώρο του ναυπηγείου και δεν βρίσκεται σε διαδικασία κατασκευής, θα πρέπει να συμπληρώνονται τα παρακάτω στοιχεία και το έντυπο να παραδίδεται στο γραφείο παραγωγής.',
      sign: 'Υπογραφή παραλήπτη',
      fields: FIELDS.el,
    },
    exit: {
      title: 'ΕΝΤΥΠΟ ΕΞΑΓΩΓΗΣ ΣΚΑΦΟΥΣ',
      instrHeading: 'Οδηγίες',
      instr:
        'Για κάθε σκάφος που εξέρχεται από τον χώρο του ναυπηγείου, θα πρέπει να συμπληρώνονται τα παρακάτω στοιχεία και το έντυπο να παραδίδεται στο γραφείο παραγωγής.',
      sign: 'Υπογραφή υπευθύνου',
      fields: FIELDS.el,
    },
  },
  en: {
    entry: {
      title: 'VESSEL ENTRY FORM',
      instrHeading: 'Instructions',
      instr:
        'For every vessel that enters the shipyard and is not under construction, fill in the details below and hand this form to the production office.',
      sign: "Recipient's signature",
      fields: FIELDS.en,
    },
    exit: {
      title: 'VESSEL EXIT FORM',
      instrHeading: 'Instructions',
      instr: 'For every vessel that leaves the shipyard, fill in the details below and hand this form to the production office.',
      sign: 'Signature of the person in charge',
      fields: FIELDS.en,
    },
  },
};

/** one line of text and its language; a second line is the first one's translation. */
export interface DocLine { text: string; lang: FormLang }

/** a form ready to draw: each piece of text is one line, or two (Greek, then English) on a bilingual sheet. */
export interface BoatDocView {
  /** the sheet's main language (the first of its lines). */
  lang: FormLang;
  title: DocLine[];
  instrHeading: string;
  instr: DocLine[];
  sign: DocLine[];
  rows: Array<{ label: DocLine[]; hint?: string; value: string }>;
}

const LANGS_OF: Record<DocLang, FormLang[]> = { el: ['el'], en: ['en'], both: ['el', 'en'] };
const bare = (t: string) => t.replace(/:\s*$/, '');

export function boatDocView(
  lang: DocLang,
  kind: BoatDocKind,
  v: { date?: string; boatName?: string; model?: string; sap?: string; phone?: string; hin?: string } = {},
): BoatDocView {
  const texts = LANGS_OF[lang].map((l) => ({ l, t: BOAT_DOC_TEXT[l][kind] }));
  // a translation line drops the label colon, and a word that reads the same in both (SAP, HIN) shows once
  const lines = (pick: (t: BoatDocText) => string): DocLine[] => {
    const out: DocLine[] = [];
    for (const [i, { l, t }] of texts.entries()) {
      const text = i === 0 ? pick(t) : bare(pick(t));
      if (out.some((o) => bare(o.text) === bare(text))) continue;
      out.push({ text, lang: l });
    }
    return out;
  };
  const field = (k: keyof BoatDocText['fields']) => lines((t) => t.fields[k]);
  return {
    lang: texts[0]!.l,
    title: lines((t) => t.title),
    instrHeading: [...new Set(texts.map((x) => x.t.instrHeading))].join(' / '),
    instr: lines((t) => t.instr),
    sign: lines((t) => t.sign),
    rows: [
      { label: field('date'), value: v.date ?? '' },
      { label: field('boatName'), value: v.boatName ?? '' },
      { label: field('model'), value: v.model ?? '' },
      { label: field('sap'), value: v.sap ?? '' },
      { label: field('phone'), value: v.phone ?? '' },
      { label: field('hin'), hint: texts[0]!.t.fields.hinHint, value: v.hin ?? '' },
    ],
  };
}

const LANG_KEY = 'rolodex.docLang';

/** the forms' language on this device as stored now (a print button outside the form reads it at the click,
 *  so a choice just made in a vessel's entry form counts). */
export function readDocLang(): DocLang {
  try {
    const v = localStorage.getItem(LANG_KEY);
    return v === 'el' || v === 'en' ? v : 'both';
  } catch {
    return 'both';
  }
}

/** the forms' language on this device (both, unless Greek or English alone was picked). */
export function useDocLang(): [DocLang, (lang: DocLang) => void] {
  const [lang, setLang] = useState<DocLang>(readDocLang);
  const set = useCallback((next: DocLang) => {
    setLang(next);
    try {
      localStorage.setItem(LANG_KEY, next);
    } catch {
      /* storage blocked: the choice lasts for this visit */
    }
  }, []);
  return [lang, set];
}
